import { resolveLine, type ResolvedLine } from './logic';

// Số liệu mẫu của Mint-Quote-iPad / -iPad-Detail / -Phone (khung xem gọn). Giá bán là giá gửi khách; giá mua chỉ dùng để gợi ý, không hiện.

/** Dòng xem ở trang chi tiết iPad: [mã, khoản, giá bán ('' = theo cách tính)]. */
export const VIEW_LINES: readonly (readonly [string, string, string])[] = [
  ['THC', 'Phí xếp dỡ tại cảng', '135 USD / cont'],
  ['SLF', 'Phí niêm chì', '10 USD / cont'],
  ['BLF', 'Phí vận đơn', '40 USD / bộ'],
  ['CUF', 'Lệ phí hải quan', ''],
  ['TRFA', 'Cước vận chuyển nội địa (đã gồm nâng hạ)', '3.200.000 ₫ / chuyến'],
  ['LNF', 'Phí nâng hạ', ''],
  ['XLH', 'Phí cầu đường', ''],
  ['ISR', 'Bảo hiểm hàng hoá', ''],
];

export interface ViewLine {
  code: string;
  label: string;
  price: string;
  line: ResolvedLine;
}

/** Dòng báo giá đã lập: dòng cước (OCF) rồi các dòng phí; "Đã bao gồm / Không bao gồm" không có giá riêng. */
export function viewLines(ocfPrice: string): ViewLine[] {
  return [['OCF', 'Cước vận tải quốc tế', ocfPrice] as const, ...VIEW_LINES].map(([code, label, p]) => {
    const line = resolveLine(code);
    return { code, label, price: line.noPrice || (!p && line.priceNote) ? line.priceNote : p, line };
  });
}

export const LOCAL_PER_CONT = 185;

// ---------- Báo giá nhanh (điện thoại) ----------

export const QUICK_CUSTOMERS: readonly (readonly [string, Readonly<Record<string, number>>])[] = [
  ['THACO', { LAX: 9 }],
  ['GREENBUD', { RTM: 7.5 }],
  ['COCOGROUP', { BUS: 7 }],
  ['SAVITA', { SYD: 10 }],
];
export const QUICK_PODS = ['LAX', 'RTM', 'BUS', 'SYD', 'MEL'] as const;
export const QUICK_TYPES = ['20DC', '40HC', '40RF'] as const;
export type QuickType = (typeof QUICK_TYPES)[number];
const POD_MARGIN: Readonly<Record<string, number>> = { LAX: 8, RTM: 7, BUS: 6, SYD: 9, MEL: 8 };
const TYPE_FACTOR: Readonly<Record<QuickType, number>> = { '20DC': 0.72, '40HC': 1, '40RF': 1.6 };

/** Giá hãng theo cảng đến: [hãng, transit, ETD, giá mua 40HC]. MEL chưa có giá hãng → gợi ý hỏi Pricing. */
const CARRIER_RATES: Readonly<Record<string, readonly (readonly [string, string, string, number])[]>> = {
  LAX: [['YML', '18 ngày · thẳng', '28/Oct', 2610], ['ONE', '17 ngày · thẳng', '30/Oct', 2680], ['HMM', '20 ngày · qua BUS', '02/Nov', 2540]],
  RTM: [['ONE', '29 ngày · qua SIN', '27/Oct', 2230], ['MSC', '31 ngày · thẳng', '31/Oct', 2180]],
  BUS: [['Wan Hai', '7 ngày · thẳng', '25/Oct', 2060], ['KMTC', '6 ngày · thẳng', '26/Oct', 2110]],
  SYD: [['ONE', '15 ngày · thẳng', '29/Oct', 1720], ['Evergreen', '16 ngày · thẳng', '01/Nov', 1690]],
  MEL: [],
};

export interface QuickOption {
  carrier: string;
  transit: string;
  etd: string;
  /** giá bán gợi ý / cont (USD) */
  sell: number;
}

/** Giá bán gợi ý = giá mua × loại cont × (1 + margin khách / tuyến), làm tròn 10. */
export function quickOptions(customer: string, pod: string, type: QuickType): QuickOption[] {
  const own = QUICK_CUSTOMERS.find(([c]) => c === customer)?.[1][pod];
  const margin = own ?? POD_MARGIN[pod] ?? 8;
  return (CARRIER_RATES[pod] ?? []).map(([carrier, transit, etd, buy]) => ({
    carrier,
    transit,
    etd,
    sell: Math.round((Math.round(buy * TYPE_FACTOR[type]) * (1 + margin / 100)) / 10) * 10,
  }));
}

/** Tổng = (giá bán + local) × số cont. */
export const quickTotal = (o: QuickOption | undefined, n: number): number => (o ? (o.sell + LOCAL_PER_CONT) * n : 0);

// ---------- Phiếu hỏi giá của một báo giá (trang chi tiết) ----------

/** Báo giá có phiếu Pricing đã trả giá (mẫu: số thứ tự chia hết cho 3). */
export const hasPricingTicket = (index: number): boolean => index % 3 === 0;
